import React, { useEffect, useState } from "react";
import { orendaUserManager } from "./orendaUserManager";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";

const decodeJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch (e) {
    return { _decodeError: e.message };
  }
};

const copy = (text) => {
  if (!text) return;
  navigator.clipboard.writeText(text);
};

const OrendaSamplePage = () => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // On mount, pick up any cached user from a previous sign-in.
  useEffect(() => {
    orendaUserManager
      .getUser()
      .then((u) => u && !u.expired && setUser(u))
      .catch(() => {});
  }, []);

  const signIn = async () => {
    setError(null);
    setBusy(true);
    try {
      // Full-page redirect (not popup) so the callback page stays visible and
      // we can inspect the raw response Cognito sends back. The browser will
      // navigate away from this page; when it returns to /orenda/login the
      // OrendaCallback component takes over.
      await orendaUserManager.signinRedirect({
        extraQueryParams: {
          identity_provider: orendaOidcConfig.identity_provider,
        },
      });
    } catch (err) {
      setError(err.message || String(err));
      setBusy(false);
    }
  };

  const signOut = async () => {
    setError(null);
    setBusy(true);
    try {
      // Full Cognito hosted-UI logout in a popup — clears the Cognito session
      // and redirects to /orenda/logout. Falls back to local-only clear if the
      // popup fails (e.g. blocked).
      try {
        await orendaUserManager.signoutPopup();
      } catch (popupErr) {
        console.warn("Popup logout failed, clearing local session only", popupErr);
      }
      await orendaUserManager.removeUser();
      setUser(null);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const accessTokenClaims = user?.access_token
    ? decodeJwt(user.access_token)
    : null;
  const idTokenClaims = user?.id_token ? decodeJwt(user.id_token) : null;

  return (
    <div className="container" style={{ padding: "40px 20px", maxWidth: 900 }}>
      <h2>Orenda Cognito Sample</h2>
      <p className="text-muted">
        Authorization Code + PKCE via <code>oidc-client-ts</code> against AWS
        Cognito (<code>bopayments.auth.eu-west-1.amazoncognito.com</code>).
      </p>

      <div className="mb-3">
        <button
          className="btn btn-primary"
          onClick={signIn}
          disabled={busy}
          style={{ marginRight: 10 }}
        >
          {busy ? "Working…" : "Sign in with Cognito (Orenda)"}
        </button>
        {user && (
          <button
            className="btn btn-outline-secondary"
            onClick={signOut}
            disabled={busy}
          >
            Sign out
          </button>
        )}
      </div>

      {error && (
        <div className="card mb-3" style={{ borderColor: "#dc3545" }}>
          <div className="card-body">
            <h5 className="card-title" style={{ color: "#dc3545" }}>
              Error
            </h5>
            <div>{error}</div>
          </div>
        </div>
      )}

      {user && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">Profile</h5>
              <div><strong>Subject:</strong> {user.profile?.sub}</div>
              <div><strong>Email:</strong> {user.profile?.email}</div>
              <div><strong>Username:</strong> {user.profile?.["cognito:username"] || user.profile?.preferred_username}</div>
              <div><strong>Name:</strong> {user.profile?.name}</div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">Token metadata</h5>
              <div><strong>Scope:</strong> {user.scope}</div>
              <div><strong>Token Type:</strong> {user.token_type}</div>
              <div>
                <strong>Expires At:</strong>{" "}
                {user.expires_at
                  ? new Date(user.expires_at * 1000).toLocaleString()
                  : "(unknown)"}
              </div>
              <div>
                <strong>Refresh Token present:</strong>{" "}
                {user.refresh_token ? "yes" : "no"}
              </div>
            </div>
          </div>

          <TokenBlock
            title="Access Token"
            token={user.access_token}
            claims={accessTokenClaims}
          />
          <TokenBlock
            title="ID Token"
            token={user.id_token}
            claims={idTokenClaims}
          />
        </>
      )}
    </div>
  );
};

const codeBlockStyle = {
  fontFamily: "monospace",
  fontSize: 12,
  wordBreak: "break-all",
  background: "#f6f8fa",
  padding: 10,
  borderRadius: 4,
};

const TokenBlock = ({ title, token, claims }) => (
  <div className="card mb-3">
    <div className="card-body">
      <h5 className="card-title">
        {title}{" "}
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => copy(token)}
          style={{ marginLeft: 10 }}
        >
          Copy
        </button>
      </h5>
      <div className="text-break" style={codeBlockStyle}>
        {token || "(empty)"}
      </div>
      <h6 style={{ marginTop: 15 }}>Decoded claims</h6>
      <pre style={{ ...codeBlockStyle, maxHeight: 300, overflow: "auto" }}>
        {JSON.stringify(claims, null, 2)}
      </pre>
    </div>
  </div>
);

export default OrendaSamplePage;
